/**
 * @module "state/form.object.state"
 * @description Manages the state information for a template object
 * @property {object} defaults The default attributes and values a newly instantiated FormObjectState model will have
 * @property {object} form a reference to a template's Form object
 * @property {object} templateObjectModel reference to the template object model of a template object
 * @property {object} answerValueVisibleValidationCollection
 * @property {object} templateObjectVisibleValidationCollection
 * @property {object} failedVisibleValidationCollection
 * @property {object} failedValidationCollection
 */

define('formkit/state/form.object.state',['require','exports','module','underscore','backbone'],function(require, exports, module) {
  //prettier-ignore
  var _        = require('underscore'),
      Backbone = require('backbone');

  /**
   * Creates a new form
   * @class FormObjectState
   * @classdesc Backbone.Model for managing the state of a specific template object
   *
   */
  var FormObjectState = Backbone.Model.extend({
    defaults: {
      hasFailedValidations: false,
      hasVisibleFailedValidations: false,
      failedVisibleValidationCollection: null,
      isTargetedByCondition: false,
      hasRules: false,
    },

    /**
     * @method initialize Supers Backbone.Model and initializes a new FormObjectState's properties and listeners
     * @constructs
     * @augments external:Backbone.Model
     * @listens {module:"model/template.model"~event:sync}
     * @listens {module:"form"~event:add:rule}
     * @listens {module:"form"~event:remove:rule}
     * @listens {module:"model/template.object.model"~event:change:helpText}
     * @fires {FormObjectState#failedValidations}
     * @param  {object} attr
     * @param  {object} options
     * @returns {undefined}
     */
    initialize: function(attrs, options) {
      Backbone.Model.prototype.initialize.apply(this, arguments);

      this.form = _.result(options, 'form');
      this.templateObjectModel = _.result(options, 'templateObjectModel');

      this.answerValueVisibleValidationCollection = new Backbone.Collection([], {
        model: Backbone.Model.extend({
          idAttribute: 'answerValueId',
        }),
      });
      this.templateObjectVisibleValidationCollection = new Backbone.Collection([], {
        model: Backbone.Model.extend({
          idAttribute: 'templateObjectId',
        }),
      });

      this.failedVisibleValidationCollection = new Backbone.Collection();
      this.set('failedVisibleValidationCollection', this.failedVisibleValidationCollection);

      this.failedValidationCollection = new Backbone.Collection();

      this.listenTo(this.form.getTemplateModel(), 'sync', function() {
        this.updateFailedValidationStates();
      });
      this.listenTo(this.form, 'add:rule remove:rule', function() {
        this.updateHasRulesState();
      });
      this.listenTo(this.templateObjectModel, 'change:helpText', function() {
        this.updateHasHelpTextState();
      });
      this.bindEventsOnEachDescendent(this.templateObjectModel);

      this.updateHasRulesState();
      this.updateIsTargetedByConditionState();
      this.updateFailedValidationStates();
      this.updateHasHelpTextState();
    },

    /**
     * updateFailedValidationStates
     * @event FormObjectState#failedValidations
     * @returns {undefined}
     */
    updateFailedValidationStates: function() {
      var failedValidations = this.getFailedValidationsArray();
      var failedVisibleValidations = this.getFailedVisibleValidationsArray(failedValidations);
      this.failedVisibleValidationCollection.set(failedVisibleValidations);
      this.set('hasFailedValidations', failedValidations.length > 0);
      this.set('hasVisibleFailedValidations', this.failedVisibleValidationCollection.length > 0);
      Mn.triggerMethodOn(this, 'failedValidations', this, failedValidations, {
        failedValidations: failedValidations,
        failedVisibleValidations: failedVisibleValidations,
      });
    },

    /**
     * showFailedValidations
     *
     * @param  {object} answerValueModel
     * @param  {object} templateObjectModel
     * @returns {object} the current context of this
     */
    showFailedValidations: function(answerValueModel, templateObjectModel) {
      var templateObjectModel = this.templateObjectModel;
      var model = this.answerValueVisibleValidationCollection.add({
        answerValueId: answerValueModel.id,
        templateObjectId: templateObjectModel.id,
        visible: true,
        answerValueModel: answerValueModel,
        templateObjectModel: templateObjectModel,
      });
      this.templateObjectVisibleValidationCollection.add(model);
      this.templateObjectVisibleValidationCollection.add({
        templateObjectId: templateObjectModel.id,
        visible: true,
        templateObjectModel: templateObjectModel,
      });
      this.updateFailedValidationStates();
      return this;
    },

    /**
     * showAllFailedValidations
     *
     * @returns {object}  the current context of this
     */
    showAllFailedValidations: function() {
      this.shoulShowAllFailedValidations = true;
      this.updateFailedValidationStates();
      return this;
    },

    /**
     * getFailedValidationsArray
     *
     * @returns {array}
     */
    getFailedValidationsArray: function() {
      var form = this.form;
      var templateObjectModel = this.templateObjectModel;
      return _.map(form.util.getFailedValidationsArray(templateObjectModel), function(failedValidation) {
        return _.extend({}, failedValidation, {
          message: form.util.getValidationMessage(failedValidation, templateObjectModel),
        });
      });
    },

    /**
     * getFailedVisibleValidationsArray
     *
     * @param  {object} failedValidations
     * @returns {array|boolean}
     */
    getFailedVisibleValidationsArray: function(failedValidations) {
      var shouldSuppressErrorMessage = function(failedValidation) {
        return failedValidation.condition.parameters.some(function(parameter) {
          return !!parameter.suppressErrorMessage;
        })
      }

      return _.filter(
        failedValidations,
        function(failedValidation) {
          var visibleValidationModel;
          if (this.shoulShowAllFailedValidations === true) {
            return true;
          } else if (shouldSuppressErrorMessage(failedValidation)){
            return false;
          } else if (failedValidation.answerValueId !== null) {
            visibleValidationModel = this.answerValueVisibleValidationCollection.get(failedValidation.answerValueId);
          } else {
            visibleValidationModel = this.templateObjectVisibleValidationCollection.get(
              failedValidation.templateObjectId
            );
          }
          return visibleValidationModel ? visibleValidationModel.get('visible') : false;
        },
        this
      );
    },

    /**
     * Creates listeners for 'add:child' and 'remove:child' and binds functionality on all descendents of a given template object
     *
     * @param  {object} model
     * @listens {module:"model/template.object.model"~event:add:child}
     * @listens {module:"model/template.object.model"~event:remove:child}
     * @returns {undefined}
     */
    bindEventsOnEachDescendent: function(model) {
      this.form.util.eachDescendent(
        model,
        function(child) {
          this.bindConditionEvents(child);

          this.listenTo(child, 'add:child', function(parent, addedChild) {
            this.bindEventsOnEachDescendent(addedChild);
          });

          this.listenTo(child, 'remove:child', function(parent, removedChild) {
            this.stopListening(this.form, 'conditionsMap:add:' + child.id + ' conditionsMap:remove:' + child.id);
            this.stopListening(removedChild, 'add:child');
            this.stopListening(removedChild, 'remove:child');
          });
        },
        this,
        { includeSelf: true }
      );
    },

    /**
     * Listens for 'conditionsMap:add' and 'conditionsMap:remove' type events on the templateObjectModel and updates the state accordingly
     *
     * @param  {object} templateObjectModel
     * @listens {module:"form"~event:conditionsMap:add:<templateObjectId>}
     * @listens {module:"form"~event:conditionsMap:remove:<templateObjectId>}
     * @returns {undefined}
     */
    bindConditionEvents: function(templateObjectModel) {
      this.listenTo(
        this.form,
        'conditionsMap:add:' + templateObjectModel.id + ' conditionsMap:remove:' + templateObjectModel.id,
        _.debounce(function() {
          this.updateIsTargetedByConditionState();
        })
      );
    },

    /**
     * Sets a boolean 'isTargetedByCondition' attribute indicating if a given template object is the target of conditions
     *
     * @returns {undefined}
     */
    updateIsTargetedByConditionState: function() {
      var model = this.templateObjectModel;
      var isTargetedByCondition = this.form.isTemplateObjectTargetedByCondition(model);
      this.set('isTargetedByCondition', isTargetedByCondition);
    },

    /**
     * Sets a boolean 'hasRules' attribute indicating if a given template object has rules
     *
     * @returns {undefined}
     */
    updateHasRulesState: function() {
      var hasRules = this.form.doesTemplateObjectHaveRules(this.templateObjectModel);
      this.set('hasRules', hasRules);
    },

    /**
     * Sets a boolean 'hasHelpText' attribute indicating if a given template object has help text
     *
     * @returns {undefined}
     */
    updateHasHelpTextState: function() {
      var templateObjectModel = this.templateObjectModel;
      var hasHelpText =
        templateObjectModel.get('helpText') && templateObjectModel.get('helpText').length ? true : false;
      this.set('hasHelpText', hasHelpText);
    },
  });

  module.exports = FormObjectState;
});

